import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import Header from '../Components/Header';
import { toast, ToastContainer } from "react-toastify";

function Client() {
  const authToken = sessionStorage.getItem('authToken');
  const rawBaseUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.marclife.io/api/admin';
  const baseurl = rawBaseUrl.trim();

  // Data States
  const [usersList, setUsersList] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
  const [isLoading, setIsLoading] = useState(false);

  // Filter States
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Selected User for Modal Quick View
  const [selectedUser, setSelectedUser] = useState(null);

  // Copy to clipboard helper
  const copyToClipboard = (text, label = 'Address') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const formatShortWallet = (wallet) => {
    if (!wallet) return 'N/A';
    if (wallet.length <= 14) return wallet;
    return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
  };

  // Fetch Users from the new API
  const fetchUsers = useCallback(async (page = 1, search = '', status = 'all') => {
    setIsLoading(true);
    try {
      const url = `${baseurl}/users?page=${page}&search=${encodeURIComponent(search)}&status=${encodeURIComponent(status)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status === 429) {
        toast.warn('Rate limit exceeded. Please wait a moment.');
        setIsLoading(false);
        return;
      }

      if (response.status === 401) {
        console.warn('API returned 401 Unauthenticated.');
        setIsLoading(false);
        return;
      }

      const result = await response.json();

      if (result.status) {
        if (result.analytics) {
          setAnalytics(result.analytics);
        }
        if (Array.isArray(result.data)) {
          setUsersList(result.data);
        }
        if (result.pagination) {
          setPagination(result.pagination);
        }
      } else {
        toast.error(result.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Network or server error while fetching users');
    } finally {
      setIsLoading(false);
    }
  }, [authToken, baseurl]);

  // Debounce search input: automatically updates appliedSearch 450ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== appliedSearch) {
        setAppliedSearch(trimmed);
        setCurrentPage(1);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [searchInput, appliedSearch]);

  // Fetch when page, search query, or status filter changes
  useEffect(() => {
    fetchUsers(currentPage, appliedSearch, statusFilter);
  }, [fetchUsers, currentPage, appliedSearch, statusFilter]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setAppliedSearch('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination.last_page || 1)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Card filter click handler
  const handleCardFilterClick = (targetStatus) => {
    if (statusFilter === targetStatus) {
      setStatusFilter('all');
    } else {
      setStatusFilter(targetStatus);
    }
    setCurrentPage(1);
  };

  // Status Badge Helper
  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
          <i className="fa fa-check-circle me-1"></i> Active
        </span>
      );
    } else if (s === 'pending') {
      return (
        <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2 py-1">
          <i className="fa fa-clock me-1"></i> Pending
        </span>
      );
    } else if (s === 'blocked' || s === 'block' || s === 'hold') {
      return (
        <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1">
          <i className="fa fa-ban me-1"></i> Blocked
        </span>
      );
    }
    return (
      <span className="badge bg-secondary-subtle text-secondary px-2 py-1">
        {status || 'Unknown'}
      </span>
    );
  };

  // Calculated active count
  const activeCount = useMemo(() => {
    const total = Number(analytics?.total_user ?? pagination?.total ?? 0);
    const pending = Number(analytics?.pending_user ?? 0);
    const blocked = Number(analytics?.blocked_user ?? 0);
    const diff = total - pending - blocked;
    return diff >= 0 ? diff : total;
  }, [analytics, pagination]);

  // Export current list to CSV
  const handleExportCSV = () => {
    if (!usersList || usersList.length === 0) {
      toast.info('No clients data to export');
      return;
    }
    const headers = [
      'ID',
      'Name',
      'Status',
      'Wallet',
      'Sponsor Wallet',
      'Deposit',
      'Total Income',
      'Available Income',
      'Withdrawn Income',
      'Direct Referrals',
      'Total Team',
      'Joined Date'
    ];
    const rows = usersList.map(u => [
      u.id,
      `"${(u.name || '').replace(/"/g, '""')}"`,
      u.status || '',
      `"${u.wallet || ''}"`,
      `"${u.sponsor_wallet || ''}"`,
      u.deposit || 0,
      u.total_income || 0,
      u.available_income || 0,
      u.withdraw_income || 0,
      u.direct_referral || 0,
      u.total_team || 0,
      u.joined || ''
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `clients_page_${pagination.current_page || 1}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Clients CSV downloaded successfully!');
  };

  const isFiltered = Boolean(searchInput || appliedSearch || statusFilter !== 'all');

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">

            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <div>
                <h4 className="mb-1 text-dark fw-bold">Clients & Users</h4>
                <p className="text-muted mb-0 fs-13">
                  Comprehensive overview of registered users, wallet connections, and network referrals.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={handleExportCSV}
                  title="Export current table to CSV"
                >
                  <i className="fa fa-download"></i> Export CSV
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                  onClick={() => fetchUsers(currentPage, appliedSearch, statusFilter)}
                  title="Refresh users"
                >
                  <i className="fa fa-sync-alt"></i> Refresh Data
                </button>
              </div>
            </div>

            {/* Top Analytics Cards (Interactive - Click to filter by status) */}
            <div className="row g-3 mb-4">
              {/* Card 1: Total Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div
                  className={`card overflow-hidden sales-card bg-primary-gradient border-0 shadow-sm h-100 mb-0 position-relative ${statusFilter === 'all' ? 'border border-2 border-white' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleCardFilterClick('all')}
                  title="Click to view all users"
                  role="button"
                >
                  <div className="px-3 pt-3 pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fs-12 text-fixed-white">TOTAL USERS</h6>
                      {statusFilter === 'all' && (
                        <span className="badge bg-white text-primary fs-10 px-1 py-0">ACTIVE FILTER</span>
                      )}
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(analytics?.total_user ?? pagination?.total ?? 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Registered clients
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-users text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Active Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div
                  className={`card overflow-hidden sales-card bg-success-gradient border-0 shadow-sm h-100 mb-0 position-relative ${statusFilter === 'active' ? 'border border-2 border-white' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleCardFilterClick('active')}
                  title="Click to filter by active users"
                  role="button"
                >
                  <div className="px-3 pt-3 pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fs-12 text-fixed-white">ACTIVE USERS</h6>
                      {statusFilter === 'active' && (
                        <span className="badge bg-white text-success fs-10 px-1 py-0">FILTERED</span>
                      )}
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {activeCount.toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Platform active clients
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-user-check text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Pending Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div
                  className={`card overflow-hidden sales-card bg-warning-gradient border-0 shadow-sm h-100 mb-0 position-relative ${statusFilter === 'pending' ? 'border border-2 border-white' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleCardFilterClick('pending')}
                  title="Click to filter by pending users"
                  role="button"
                >
                  <div className="px-3 pt-3 pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fs-12 text-fixed-white">PENDING USERS</h6>
                      {statusFilter === 'pending' && (
                        <span className="badge bg-white text-warning fs-10 px-1 py-0">FILTERED</span>
                      )}
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(analytics?.pending_user ?? 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Awaiting activation
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-user-clock text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: Blocked Users */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                <div
                  className={`card overflow-hidden sales-card bg-danger-gradient border-0 shadow-sm h-100 mb-0 position-relative ${statusFilter === 'blocked' ? 'border border-2 border-white' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleCardFilterClick('blocked')}
                  title="Click to filter by blocked users"
                  role="button"
                >
                  <div className="px-3 pt-3 pb-2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0 fs-12 text-fixed-white">BLOCKED USERS</h6>
                      {statusFilter === 'blocked' && (
                        <span className="badge bg-white text-danger fs-10 px-1 py-0">FILTERED</span>
                      )}
                    </div>
                    <div className="pb-0 mt-0">
                      <div className="d-flex">
                        <div>
                          <h4 className="fs-20 fw-bold mb-1 text-fixed-white">
                            {Number(analytics?.blocked_user ?? 0).toLocaleString()}
                          </h4>
                          <p className="mb-0 fs-12 text-fixed-white op-7">
                            Restricted or blocked
                          </p>
                        </div>
                        <span className="float-end my-auto ms-auto">
                          <i className="fas fa-user-slash text-fixed-white fs-20" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters Bar Card */}
            <div className="card border-0 shadow-sm mb-4 rounded-3">
              <div className="card-body p-3">
                <form onSubmit={handleSearchSubmit}>
                  <div className="row g-2 align-items-center">
                    {/* Search Field */}
                    <div className="col-lg-6 col-md-6 col-12">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white border-end-0 text-muted">
                          <i className="fa fa-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search by name, ID, wallet, or sponsor..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                        />
                        {searchInput && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary border-start-0"
                            onClick={() => {
                              setSearchInput('');
                              setAppliedSearch('');
                              setCurrentPage(1);
                            }}
                            title="Clear search"
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Status Dropdown */}
                    <div className="col-lg-3 col-md-3 col-6">
                      <select
                        className="form-select form-select-sm"
                        value={statusFilter}
                        onChange={(e) => {
                          setStatusFilter(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="all">All Status ({Number(analytics?.total_user ?? pagination?.total ?? 0)})</option>
                        <option value="active">Active ({activeCount})</option>
                        <option value="pending">Pending ({Number(analytics?.pending_user ?? 0)})</option>
                        <option value="blocked">Blocked ({Number(analytics?.blocked_user ?? 0)})</option>
                      </select>
                    </div>

                    {/* Action Buttons */}
                    <div className="col-lg-3 col-md-3 col-6 d-flex gap-2 justify-content-end">
                      <button type="submit" className="btn btn-sm btn-primary flex-grow-1">
                        <i className="fa fa-filter me-1"></i> Filter
                      </button>
                      {isFiltered && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleResetFilters}
                          title="Reset all filters"
                        >
                          <i className="fa fa-undo me-1"></i> Reset
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Users Table Card */}
            <div className="card  card-body border-0 shadow-sm rounded-3 mb-4">
              <div className="card-header bg-white border-bottom py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <i className="fa fa-users text-primary"></i>
                    <span>Registered Clients</span>
                  </h6>
                  {statusFilter !== 'all' && (
                    <span className="badge bg-light text-primary border text-capitalize">
                      Status: {statusFilter}
                    </span>
                  )}
                  {appliedSearch && (
                    <span className="badge bg-light text-muted border">
                      Search: "{appliedSearch}"
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-light text-muted border fs-12">
                    Total: {pagination.total || usersList.length} Clients
                  </span>
                  <span className="badge bg-light text-muted border fs-12">
                    Page {pagination.current_page || 1} of {pagination.last_page || 1}
                  </span>
                </div>
              </div>

              <div className="table-responsive">
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2 fs-13">Loading client records...</p>
                  </div>
                ) : usersList.length > 0 ? (
                  <table className="table table-hover align-middle mb-0 text-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '45px' }}>Avatar</th>
                        <th>Client & ID</th>
                        <th>Wallet Address</th>
                        <th>Sponsor Wallet</th>
                        <th>Deposit</th>
                        <th>Total Income</th>
                        <th>Team Network</th>
                        <th>Status</th>
                        <th>Joined</th>
                        <th className="text-end" style={{ width: '110px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersList.map((user, index) => (
                        <tr key={user.id || index}>
                          <td>
                            <img
                              className="avatar avatar-md rounded-circle border"
                              src={user.profile || 'assets/images/profile/profile1.avif'}
                              alt={user.name || 'Client Avatar'}
                              onError={(e) => {
                                e.target.src = 'assets/images/profile/profile1.avif';
                              }}
                            />
                          </td>
                          <td>
                            <div className="fw-semibold text-dark">{user.name || 'Unnamed Client'}</div>
                            <span className="text-muted small fs-12">ID: #{user.id || 'N/A'}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <span className="font-monospace text-dark fs-13" title={user.wallet}>
                                {formatShortWallet(user.wallet)}
                              </span>
                              {user.wallet && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link p-0 text-muted"
                                  onClick={() => copyToClipboard(user.wallet, 'Wallet')}
                                  title="Copy wallet address"
                                >
                                  <i className="fa fa-copy fs-11"></i>
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              {user.sponsor_wallet && user.sponsor_wallet !== '0x0000000000000000000000000000000000000000' ? (
                                <>
                                  <span className="font-monospace text-muted fs-13" title={user.sponsor_wallet}>
                                    {formatShortWallet(user.sponsor_wallet)}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0 text-muted"
                                    onClick={() => copyToClipboard(user.sponsor_wallet, 'Sponsor wallet')}
                                    title="Copy sponsor wallet"
                                  >
                                    <i className="fa fa-copy fs-11"></i>
                                  </button>
                                </>
                              ) : (
                                <span className="badge bg-light text-muted border">Direct / None</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="fw-bold text-dark" title={`$${user.deposit || 0}`}>
                              {Number(user.deposit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                            </span>
                          </td>
                          <td>
                            <span className="fw-semibold text-success" title={`$${user.total_income || 0}`}>
                              {Number(user.total_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <span className="badge bg-light text-dark border" title={`Total Team: ${user.total_team || 0} (${user.total_active || 0} Active)`}>
                                <i className="fa fa-users text-primary me-1"></i> {user.total_team || 0}
                              </span>
                              {user.direct_referral > 0 && (
                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle fs-11" title={`Direct: ${user.direct_referral} (${user.direct_active || 0} Active)`}>
                                  Dir: {user.direct_referral}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            {getStatusBadge(user.status)}
                          </td>
                          <td>
                            <span className="text-muted fs-13">
                              {user.joined || 'N/A'}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="btn-group btn-group-sm">
                              {/* Quick View Modal Trigger */}
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                title="Quick Financial View"
                                data-bs-toggle="modal"
                                data-bs-target="#clientQuickViewModal"
                                onClick={() => setSelectedUser(user)}
                              >
                                <i className="fa fa-eye"></i>
                              </button>
                              {/* Full Profile Details Page */}
                              <Link
                                to="/clientsDetails"
                                onClick={() => { sessionStorage.setItem("clientId", user.id); }}
                                className="btn btn-outline-info"
                                title="Full Client Management Page"
                              >
                                <i className="fa fa-user"></i>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-5">
                    <div
                      className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center text-muted mb-3"
                      style={{ width: '64px', height: '64px' }}
                    >
                      <i className="fa fa-user-slash fs-3"></i>
                    </div>
                    <h6 className="fw-semibold text-dark">No clients found</h6>
                    <p className="text-muted fs-13 mb-3">
                      {isFiltered
                        ? 'No client accounts match your current filter parameters.'
                        : 'No clients found in the system.'}
                    </p>
                    {isFiltered && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleResetFilters}
                      >
                        <i className="fa fa-undo me-1"></i> Reset Filters
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* GridJS Style Pagination */}
              {pagination.total > 0 && (
                <div className="gridjs-footer border-top p-3">
                  <div className="gridjs-pagination d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div role="status" aria-live="polite" className="gridjs-summary fs-13 text-muted">
                      Showing <b>{(pagination.current_page - 1) * pagination.per_page + 1}</b> to{' '}
                      <b>{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</b> of{' '}
                      <b>{pagination.total}</b> results
                    </div>
                    <div className="gridjs-pages text-end">
                      {/* Previous Button */}
                      <button
                        title="Previous"
                        aria-label="Previous"
                        className={pagination.current_page === 1 ? "disabled" : ""}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                      >
                        Previous
                      </button>

                      {/* First Page */}
                      <button
                        className={pagination.current_page === 1 ? "gridjs-currentPage" : ""}
                        title="Page 1"
                        aria-label="Page 1"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>

                      {/* Ellipsis if needed */}
                      {pagination.current_page > 3 && <span>...</span>}

                      {/* Middle Pages */}
                      {Array.from({ length: 5 }, (_, i) => {
                        const page = pagination.current_page - 2 + i;
                        if (page > 1 && page < pagination.last_page) {
                          return (
                            <button
                              key={page}
                              className={pagination.current_page === page ? "gridjs-currentPage" : ""}
                              title={`Page ${page}`}
                              aria-label={`Page ${page}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        }
                        return null;
                      })}

                      {/* Ellipsis if needed */}
                      {pagination.current_page < pagination.last_page - 2 && <span>...</span>}

                      {/* Last Page */}
                      {pagination.last_page > 1 && (
                        <button
                          className={pagination.current_page === pagination.last_page ? "gridjs-currentPage" : ""}
                          title={`Page ${pagination.last_page}`}
                          aria-label={`Page ${pagination.last_page}`}
                          onClick={() => handlePageChange(pagination.last_page)}
                        >
                          {pagination.last_page}
                        </button>
                      )}

                      {/* Next Button */}
                      <button
                        title="Next"
                        aria-label="Next"
                        className={pagination.current_page === pagination.last_page ? "disabled" : ""}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* MODAL: Client Quick View & Financial Summary */}
        <div className="modal fade" id="clientQuickViewModal" tabIndex="-1" aria-labelledby="clientQuickViewModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header bg-light">
                <h6 className="modal-title fw-bold text-dark" id="clientQuickViewModalLabel">
                  <i className="fa fa-id-card text-primary me-2"></i>Client Profile & Financial Overview
                </h6>
                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div className="modal-body p-4">
                {selectedUser ? (
                  <>
                    {/* Header info */}
                    <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 mb-4">
                      <img
                        className="avatar avatar-xl rounded-circle border"
                        src={selectedUser.profile || 'assets/images/profile/profile1.avif'}
                        alt={selectedUser.name || 'User Profile'}
                        onError={(e) => { e.target.src = 'assets/images/profile/profile1.avif'; }}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between">
                          <h5 className="fw-bold mb-1 text-dark">{selectedUser.name || 'Unnamed Client'}</h5>
                          {getStatusBadge(selectedUser.status)}
                        </div>
                        <p className="text-muted small mb-1">Account ID: #{selectedUser.id} &bull; Joined: {selectedUser.joined || 'N/A'}</p>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <span className="font-monospace fs-12 text-muted">Wallet: {formatShortWallet(selectedUser.wallet)}</span>
                          {selectedUser.wallet && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-primary"
                              onClick={() => copyToClipboard(selectedUser.wallet, 'Wallet')}
                              title="Copy full wallet address"
                            >
                              <i className="fa fa-copy"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Primary Balances */}
                    <h6 className="fw-bold fs-13 text-dark mb-3">Core Account Balances</h6>
                    <div className="row g-2 mb-4">
                      <div className="col-md-3 col-6">
                        <div className="border rounded-2 p-2 bg-white">
                          <span className="text-muted fs-11 text-uppercase">Total Deposit</span>
                          <h6 className="fw-bold mb-0 text-dark mt-1">{Number(selectedUser.deposit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</h6>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="border rounded-2 p-2 bg-white">
                          <span className="text-muted fs-11 text-uppercase">Total Income</span>
                          <h6 className="fw-bold mb-0 text-success mt-1">{Number(selectedUser.total_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</h6>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="border rounded-2 p-2 bg-white">
                          <span className="text-muted fs-11 text-uppercase">Available Income</span>
                          <h6 className="fw-bold mb-0 text-primary mt-1">{Number(selectedUser.available_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</h6>
                        </div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="border rounded-2 p-2 bg-white">
                          <span className="text-muted fs-11 text-uppercase">Withdrawn</span>
                          <h6 className="fw-bold mb-0 text-warning mt-1">{Number(selectedUser.withdraw_income || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</h6>
                        </div>
                      </div>
                    </div>

                    {/* Detailed Income Streams */}
                    <h6 className="fw-bold fs-13 text-dark mb-3">Income Breakdown</h6>
                    <div className="row g-2 mb-4">
                      <div className="col-md-2 col-4">
                        <div className="border rounded-2 p-2 bg-white text-center">
                          <span className="text-muted fs-11 text-uppercase">Direct</span>
                          <p className="fw-semibold mb-0 text-dark fs-13 mt-1">{Number(selectedUser.direct_income || 0).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="border rounded-2 p-2 bg-white text-center">
                          <span className="text-muted fs-11 text-uppercase">Level</span>
                          <p className="fw-semibold mb-0 text-dark fs-13 mt-1">{Number(selectedUser.level_income || 0).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="border rounded-2 p-2 bg-white text-center">
                          <span className="text-muted fs-11 text-uppercase">ROI</span>
                          <p className="fw-semibold mb-0 text-dark fs-13 mt-1">{Number(selectedUser.roi_income || 0).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="border rounded-2 p-2 bg-white text-center">
                          <span className="text-muted fs-11 text-uppercase">Salary</span>
                          <p className="fw-semibold mb-0 text-dark fs-13 mt-1">{Number(selectedUser.salary_income || 0).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="border rounded-2 p-2 bg-white text-center">
                          <span className="text-muted fs-11 text-uppercase">Reward</span>
                          <p className="fw-semibold mb-0 text-dark fs-13 mt-1">{Number(selectedUser.reward_income || 0).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="col-md-2 col-4">
                        <div className="border rounded-2 p-2 bg-white text-center">
                          <span className="text-muted fs-11 text-uppercase">Smart</span>
                          <p className="fw-semibold mb-0 text-dark fs-13 mt-1">{Number(selectedUser.smart_income || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Team & Referrals */}
                    <h6 className="fw-bold fs-13 text-dark mb-2">Network & Referrals</h6>
                    <div className="row g-2 mb-3">
                      <div className="col-md-6 col-12">
                        <div className="border rounded-2 p-3 bg-light">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted fs-12">Direct Referrals</span>
                            <span className="fw-semibold text-dark fs-12">{selectedUser.direct_referral || 0} ({selectedUser.direct_active || 0} Active)</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted fs-12">Direct Business</span>
                            <span className="fw-semibold text-dark fs-12">${Number(selectedUser.direct_business || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-12">
                        <div className="border rounded-2 p-3 bg-light">
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted fs-12">Total Team</span>
                            <span className="fw-semibold text-dark fs-12">{selectedUser.total_team || 0} ({selectedUser.total_active || 0} Active)</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted fs-12">Team Business</span>
                            <span className="fw-semibold text-dark fs-12">${Number(selectedUser.team_business || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sponsor Wallet Info */}
                    {selectedUser.sponsor_wallet && (
                      <div className="p-2 border rounded-2 bg-white d-flex align-items-center justify-content-between">
                        <div>
                          <span className="text-muted fs-11 d-block">Sponsor Wallet Address</span>
                          <span className="font-monospace fs-12 text-dark">{selectedUser.sponsor_wallet}</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => copyToClipboard(selectedUser.sponsor_wallet, 'Sponsor wallet')}
                          title="Copy sponsor wallet"
                        >
                          <i className="fa fa-copy me-1"></i> Copy
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted">No client selected.</p>
                )}
              </div>
              <div className="modal-footer bg-light d-flex justify-content-between">
                <button type="button" className="btn btn-sm btn-outline-secondary" data-bs-dismiss="modal">Close</button>
                {selectedUser && (
                  <Link
                    to="/clientsDetails"
                    onClick={() => sessionStorage.setItem("clientId", selectedUser.id)}
                    className="btn btn-sm btn-primary"
                    data-bs-dismiss="modal"
                  >
                    <i className="fa fa-user me-1"></i> Open Full Client Details Page
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        <ToastContainer />
      </Header>
      <Footer />
    </>
  );
}

export default Client;